import React, { useMemo } from "react";
import { Target, TrendingUp, Star, BookOpen, GraduationCap, Users } from "lucide-react";

const ProgressJourney = ({ currentStep = 1, totalSteps = 6 }) => {
  const journeySteps = useMemo(() => [
    { id: 1, icon: <Target className="w-4 h-4" />, title: "AI Job Impact Assessment" },
    { id: 2, icon: <TrendingUp className="w-4 h-4" />, title: "Career Recommendations" },
    { id: 3, icon: <Star className="w-4 h-4" />, title: "Career Selection" },
    { id: 4, icon: <BookOpen className="w-4 h-4" />, title: "AI Literacy Assessment" },
    { id: 5, icon: <GraduationCap className="w-4 h-4" />, title: "Generate Upskilling Pathway" },
    { id: 6, icon: <Users className="w-4 h-4" />, title: "Upskilling and Connecting" }
  ], []);

  const getStepState = useMemo(() => (stepId) => {
    if (stepId < currentStep) return { isCompleted: true, isActive: false };
    if (stepId === currentStep) return { isCompleted: false, isActive: true };
    return { isCompleted: false, isActive: false };
  }, [currentStep]);

  const getDotColor = useMemo(() => (stepId) => {
    if (stepId === currentStep) return 'bg-blue-800';
    if (stepId < currentStep) return 'bg-green-500';
    return 'bg-gray-300';
  }, [currentStep]);

  const getDotStyle = useMemo(() => (stepId) => {
    if (stepId < currentStep) return { backgroundColor: '#22c55e' }; // green-500
    return {};
  }, []);

  const currentStepData = useMemo(() => 
    journeySteps.find(step => step.id === currentStep), [journeySteps, currentStep]
  );

  return (
    <div className="border border-gray-400 rounded-xl p-3 sm:p-4 bg-white shadow-sm w-full">
      <div className="space-y-4 sm:space-y-6">
        {/* Top Section - Progress Bar */}
        <div className="space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm sm:text-base font-semibold text-gray-500">Your Journey:</span>
              <div className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 mt-1">
                {currentStepData?.icon || <Target className="w-4 h-4 sm:w-5 sm:h-5" />}
              </div>
            </div>
            <span className="text-sm sm:text-base font-bold text-gray-700">
              {currentStepData?.title || "AI Job Impact Assessment"}
            </span>
            <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-full w-fit">
              You are here
            </span>
          </div>
          
          {/* Progress Line */}
          <div className="relative">
            <div className="flex items-center">
              <div className={`w-2 h-2 rounded-full ${getDotColor(1)}`} style={getDotStyle(1)}></div>
              <div className="flex items-center gap-1 ml-2">
                <div className="w-2 sm:w-3 h-0.5 bg-gray-300"></div>
                <div className={`w-2 h-2 rounded-full ${getDotColor(2)}`} style={getDotStyle(2)}></div>
                <div className="w-2 sm:w-3 h-0.5 bg-gray-300"></div>
                <div className={`w-2 h-2 rounded-full ${getDotColor(3)}`} style={getDotStyle(3)}></div>
                <div className="w-2 sm:w-3 h-0.5 bg-gray-300"></div>
                <div className={`w-2 h-2 rounded-full ${getDotColor(4)}`} style={getDotStyle(4)}></div>
                <div className="w-2 sm:w-3 h-0.5 bg-gray-300"></div>
                <div className={`w-2 h-2 rounded-full ${getDotColor(5)}`} style={getDotStyle(5)}></div>
                <div className="w-2 sm:w-3 h-0.5 bg-gray-300"></div>
                <div className={`w-2 h-2 rounded-full ${getDotColor(6)}`} style={getDotStyle(6)}></div>
              </div>
            </div>
          </div>
          
          {/* Separate line below dots connecting to journey steps */}
          <div className="relative -mx-3 mt-4">
            <div className="absolute inset-x-0 h-0.5 bg-gray-300"></div>
          </div>
        </div>

        {/* Bottom Section - Journey Steps */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-1">
          {journeySteps.map((step) => {
            const { isCompleted, isActive } = getStepState(step.id);
            return (
              <div key={step.id} className="text-center">
                <div className={`mx-auto mb-2 p-2 sm:p-2 rounded-lg ${
                  isActive ? 'bg-blue-100 border border-blue-300' : 'bg-white'
                }`}>
                  <div className={`mx-auto w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${
                    isActive ? 'bg-blue-800 text-white' 
                    : isCompleted ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                  }`}>
                    {React.cloneElement(step.icon, { 
                      className: "w-3 h-3 sm:w-4 sm:h-4" 
                    })}
                  </div>
                  <div className={`text-xs sm:text-xs font-medium leading-tight mt-1 sm:mt-2 ${
                    isActive ? 'text-blue-800 font-semibold' 
                    : isCompleted ? 'text-green-500 font-semibold'
                    : 'text-gray-600'
                  }`}>
                    {step.title}
                  </div>
                  {isActive && (
                    <div className="mt-1 text-xs text-blue-800 font-medium">
                      ← You are here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgressJourney; 